import { useEffect, useRef } from 'react';
import isUndefined from 'lodash/isUndefined';
import type { SingleValue } from 'react-select';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { useCheckOnFocus } from '@hooks/useCheckOnFocus';
import { TranslationValues } from '@chaika/i18n/types';

import { createDefaultOption, isSameOptions, makeValue, resolvePlaceholder } from '../utils/selectHelpers';
import type { OptionType } from '../Select';
import type { DefaultOption } from '../constants/constants';

interface Params {
    options: OptionType[];
    value: Nullable<OptionType>;
    selectedValue: Nullable<string | number> | undefined;
    withDefaultValue: boolean | undefined;
    defaultOptionType: DefaultOption | TranslationValues | undefined;
    forcePlaceholder: boolean | undefined;
    placeholder: string | undefined;
    title: string | undefined;
    name: string | undefined;
    resetValueOnUpdateOption: boolean | undefined;
    onChange(params: Nullable<OptionType>, name?: string): void;
}

interface Data {
    isOnFocus: boolean;
    options: OptionType[];
    value: Nullable<OptionType>;
    placeholder: string | undefined;
    handleChange(selectedItem: SingleValue<OptionType>): void;
    handleFocus(): void;
    handleBlur(): void;
}

export function useSelect({
    options,
    value,
    selectedValue,
    withDefaultValue,
    defaultOptionType,
    forcePlaceholder,
    placeholder,
    title,
    name,
    resetValueOnUpdateOption,
    onChange,
}: Params): Data {
    const { isOnFocus, handleFocus, handleBlur } = useCheckOnFocus();

    const previousOptionsRef = useRef(options);

    useEffect(() => {
        if (resetValueOnUpdateOption && !isSameOptions(previousOptionsRef.current, options)) {
            onChange(null);
        }
        previousOptionsRef.current = options;
    }, [options, resetValueOnUpdateOption, onChange]);

    const optionsWithDefault = defaultOptionType ? [createDefaultOption(defaultOptionType), ...options] : options;

    const resolvedValue =
        !isUndefined(selectedValue) || withDefaultValue
            ? makeValue(optionsWithDefault, selectedValue, withDefaultValue)
            : value;

    return {
        isOnFocus,
        options: optionsWithDefault,
        value: resolvedValue,
        placeholder: resolvePlaceholder({ forcePlaceholder, placeholder, title, isOnFocus }),
        handleChange: (selectedItem: SingleValue<OptionType>) => onChange(selectedItem, name),
        handleFocus,
        handleBlur,
    };
}
